import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiSend, FiCreditCard, FiXCircle, FiCopy, FiDownload, FiPrinter, FiTrash2,
  FiAlertTriangle, FiClock, FiRotateCcw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, ConfirmDialog, Spinner } from '../../components/common';
import InvoicePdfFrame from './components/InvoicePdfFrame';
import InvoiceTotalsPanel from './components/InvoiceTotalsPanel';
import RecordPaymentModal from './components/RecordPaymentModal';
import CancelInvoiceModal from './components/CancelInvoiceModal';
import { invoiceService } from '../../services/invoiceService';
import { billingFiles } from '../../services/billingFiles';
import { getApiError } from '../../services/cmsService';
import { formatDate, getRelativeTime } from '../../utils/dateUtils';
import { InvoiceStatusBadge, formatDMY, formatINR, paymentModeLabel } from './billingUtils';
import './Billing.css';
import './InvoiceDetailPage.css';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfVersion, setPdfVersion] = useState(0);
  const [busy, setBusy] = useState(null);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReinstate, setConfirmReinstate] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setInvoice(await invoiceService.getById(id));
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load the invoice'));
      navigate('/billing/invoices', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  /**
   * Mutation responses carry the new figures but not the activity log or the
   * download filename, so take them at once and then reload the full detail.
   * The server regenerates the PDF, so refetch that too.
   */
  const afterChange = (updated) => {
    setInvoice((prev) => ({ ...prev, ...updated, activity: prev.activity, downloadFilename: prev.downloadFilename, client: prev.client }));
    setPdfVersion((v) => v + 1);
    load();
  };

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (error) {
      toast.error(getApiError(error, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  };

  const doIssue = () =>
    run('issue', async () => {
      const issued = await invoiceService.issue(invoice.id);
      toast.success(`Invoice ${issued.number} issued`);
      afterChange(issued);
    });

  const doDuplicate = () =>
    run('duplicate', async () => {
      const copy = await invoiceService.duplicate(invoice.id);
      toast.success('Duplicate draft created');
      navigate(`/billing/invoices/${copy.id}/edit`);
    });

  const doReinstate = () =>
    run('reinstate', async () => {
      const reinstated = await invoiceService.reinstate(invoice.id);
      toast.success(`Invoice ${reinstated.number} reinstated`);
      afterChange(reinstated);
    });

  const doDelete = () =>
    run('delete', async () => {
      await invoiceService.delete(invoice.id);
      toast.success(invoice.status === 'draft' ? 'Draft deleted' : `Invoice ${invoice.number} deleted`);
      navigate('/billing/invoices');
    });

  const doDeletePayment = () =>
    run('payment', async () => {
      const updated = await invoiceService.deletePayment(paymentToDelete.id);
      toast.success('Payment removed');
      afterChange(updated);
    });

  const download = () => run('download', () => billingFiles.downloadPdf(invoice.id, invoice.downloadFilename));
  const print = () => run('print', () => billingFiles.printPdf(invoice.id));

  if (loading || !invoice) {
    return (
      <div className="bl-center">
        <Spinner size="large" />
      </div>
    );
  }

  const { permissions } = invoice;
  const isDraft = invoice.status === 'draft';
  const isCancelled = invoice.status === 'cancelled';

  return (
    <div className="bl-page invoice-detail">
      <Link to="/billing/invoices" className="bl-back">
        <FiArrowLeft /> Back to invoices
      </Link>

      <div className="id-header">
        <div className="id-title">
          <h1>{invoice.number || 'Draft invoice'}</h1>
          <div className="id-title-meta">
            <InvoiceStatusBadge status={invoice.status} isOverdue={invoice.isOverdue} size="medium" />
            {invoice.clientId ? (
              <Link to={`/billing/clients/${invoice.clientId}`} className="bl-link">{invoice.clientName}</Link>
            ) : (
              <span>{invoice.clientName}</span>
            )}
          </div>
        </div>

        <div className="bl-header-actions">
          {permissions.canEdit && (
            <Button variant="outline" icon={<FiEdit2 />} onClick={() => navigate(`/billing/invoices/${invoice.id}/edit`)}>
              Edit
            </Button>
          )}
          {permissions.canIssue && (
            <Button icon={<FiSend />} onClick={() => setConfirmIssue(true)} loading={busy === 'issue'}>
              Issue
            </Button>
          )}
          {permissions.canRecordPayment && (
            <Button variant="success" icon={<FiCreditCard />} onClick={() => setPaymentOpen(true)}>
              Record payment
            </Button>
          )}
          <Button variant="outline" icon={<FiDownload />} onClick={download} loading={busy === 'download'}>
            Download
          </Button>
          <Button variant="outline" icon={<FiPrinter />} onClick={print} loading={busy === 'print'}>
            Print
          </Button>
          <Button variant="ghost" icon={<FiCopy />} onClick={doDuplicate} loading={busy === 'duplicate'}>
            Duplicate
          </Button>
          {permissions.canCancel && (
            <Button variant="ghost" icon={<FiXCircle />} onClick={() => setCancelOpen(true)}>
              Cancel invoice
            </Button>
          )}
          {permissions.canReinstate && (
            <Button variant="outline" icon={<FiRotateCcw />} onClick={() => setConfirmReinstate(true)} loading={busy === 'reinstate'}>
              Reinstate
            </Button>
          )}
          {permissions.canDelete && (
            <Button variant="ghost" icon={<FiTrash2 />} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {isCancelled && (
        <div className="bl-banner danger" role="status">
          <FiXCircle />
          <div className="bl-banner-body">
            <strong>Cancelled{invoice.cancelledAt ? ` on ${formatDate(invoice.cancelledAt)}` : ''}.</strong>{' '}
            {invoice.cancelReason && <>Reason: {invoice.cancelReason}</>}
          </div>
        </div>
      )}

      {isDraft && !invoice.taxType && (
        <div className="bl-banner warning">
          <FiAlertTriangle />
          <div className="bl-banner-body">
            Client state unknown — tax is not applied and the draft can’t be issued.{' '}
            {invoice.clientId && <Link to={`/billing/clients/${invoice.clientId}`}>Edit client</Link>}
          </div>
        </div>
      )}

      {invoice.client?.isDeleted && (
        <div className="bl-banner info">
          <FiAlertTriangle />
          <div className="bl-banner-body">This client has since been deleted. The invoice keeps its own copy of the client details.</div>
        </div>
      )}

      <div className="id-layout">
        <Card padding="small" className="id-pdf-card">
          <InvoicePdfFrame invoiceId={invoice.id} version={pdfVersion} />
        </Card>

        <div className="id-side">
          <Card title="Summary" padding="medium">
            <dl className="bl-info-grid id-meta">
              <div>
                <dt>Invoice date</dt>
                <dd>{formatDMY(invoice.invoiceDate)}</dd>
              </div>
              <div>
                <dt>Due date</dt>
                <dd className={invoice.isOverdue ? 'id-overdue' : ''}>{formatDMY(invoice.dueDate)}</dd>
              </div>
              <div>
                <dt>GSTIN</dt>
                <dd className="bl-mono">{invoice.clientGstin || '—'}</dd>
              </div>
              <div>
                <dt>Client ID</dt>
                <dd className="bl-mono">{invoice.clientCode || '—'}</dd>
              </div>
            </dl>
            <div className="id-totals">
              <InvoiceTotalsPanel totals={{ ...invoice, halfRate: null }} />
            </div>
          </Card>

          {!isDraft && (
            <Card
              title="Payments"
              padding="medium"
              action={
                permissions.canRecordPayment && (
                  <Button size="small" variant="outline" icon={<FiCreditCard />} onClick={() => setPaymentOpen(true)}>
                    Add
                  </Button>
                )
              }
            >
              {invoice.payments.length === 0 ? (
                <div className="bl-empty">No payments recorded yet.</div>
              ) : (
                <div className="bl-table-wrap">
                  <table className="bl-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Mode</th>
                        <th className="num">Amount</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments.map((p) => (
                        <tr key={p.id}>
                          <td>
                            {formatDMY(p.paymentDate)}
                            {(p.reference || p.notes) && (
                              <div className="bl-cell-sub">{[p.reference, p.notes].filter(Boolean).join(' · ')}</div>
                            )}
                          </td>
                          <td>{paymentModeLabel(p.mode)}</td>
                          <td className="num">{formatINR(p.amount)}</td>
                          <td>
                            {!isCancelled && (
                              <button
                                type="button"
                                className="action-btn delete"
                                title="Delete payment"
                                onClick={() => setPaymentToDelete(p)}
                              >
                                <FiTrash2 />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          <Card title="History" padding="medium">
            {invoice.activity.length === 0 ? (
              <div className="bl-empty">No activity yet.</div>
            ) : (
              <ol className="id-timeline">
                {[...invoice.activity].reverse().map((entry, index) => (
                  <li key={`${entry.action}-${entry.createdAt}-${index}`} className={`id-event is-${entry.action}`}>
                    <span className="id-event-dot" aria-hidden="true" />
                    <div className="id-event-body">
                      <span className="id-event-text">{entry.description}</span>
                      <span className="bl-cell-sub">
                        {entry.user && <>{entry.user} · </>}
                        <span title={entry.createdAt ? formatDate(entry.createdAt, 'datetime') : ''}>
                          <FiClock className="id-inline-icon" /> {entry.createdAt ? getRelativeTime(entry.createdAt) : ''}
                        </span>
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      <RecordPaymentModal isOpen={paymentOpen} onClose={() => setPaymentOpen(false)} invoice={invoice} onRecorded={afterChange} />
      <CancelInvoiceModal isOpen={cancelOpen} onClose={() => setCancelOpen(false)} invoice={invoice} onCancelled={afterChange} />

      <ConfirmDialog
        isOpen={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={doIssue}
        title="Issue this invoice?"
        message={`This assigns the next invoice number and locks the invoice for ${formatINR(invoice.totalAmount)}. Corrections require cancelling it.`}
        confirmText="Issue invoice"
        variant="primary"
        loading={busy === 'issue'}
      />
      <ConfirmDialog
        isOpen={confirmReinstate}
        onClose={() => setConfirmReinstate(false)}
        onConfirm={doReinstate}
        title={`Reinstate invoice ${invoice.number ?? ''}?`}
        message="This undoes the cancellation. The invoice goes back to issued with the same number, and its projects are marked invoiced again."
        confirmText="Reinstate invoice"
        variant="primary"
        loading={busy === 'reinstate'}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title={isDraft ? 'Delete draft' : `Delete invoice ${invoice.number}`}
        message={
          isDraft
            ? 'Delete this draft invoice? Its projects stay pending. This cannot be undone.'
            : `Delete cancelled invoice ${invoice.number}? It disappears from the invoice list and can no longer be reinstated. Its number is not reused.`
        }
        confirmText={isDraft ? 'Delete draft' : 'Delete invoice'}
        loading={busy === 'delete'}
      />
      <ConfirmDialog
        isOpen={Boolean(paymentToDelete)}
        onClose={() => setPaymentToDelete(null)}
        onConfirm={doDeletePayment}
        title="Delete payment"
        message={`Remove the ${formatINR(paymentToDelete?.amount)} payment of ${formatDMY(paymentToDelete?.paymentDate)}? The balance and status are recalculated.`}
        confirmText="Delete payment"
        loading={busy === 'payment'}
      />
    </div>
  );
};

export default InvoiceDetailPage;
